/**
 * GameState.ts — Central mutable game state container.
 *
 * All runtime game state lives here. Supports serialization for save/load
 * and emits events via EventSystem on significant state changes.
 */

import type { ClassId, StatName } from '@game/data/types';
import { getClass } from '@game/data/classes';
import { EventSystem } from './EventSystem';

// ─── Trait State ──────────────────────────────────────────────

export interface TraitState {
  name: string;
  level: number;
  description: string;
  category: string;
}

// ─── Choice History Entry ─────────────────────────────────────

export interface ChoiceHistoryEntry {
  nodeId: string;
  choiceId: string;
  timestamp: number;
}

// ─── Character State ──────────────────────────────────────────

export interface CharacterState {
  name: string;
  classId: ClassId;
  level: number;
  xp: number;
  hp: number;
  hpMax: number;
  frostbite: number;
  corruption: number;
  stats: Record<StatName, number>;
  unspentStatPoints: number;
}

// ─── Equipped Slots ───────────────────────────────────────────

export interface EquippedSlots {
  weapon?: string;
  armor?: string;
  accessory?: string;
}

// ─── Story State ──────────────────────────────────────────────

export interface StoryState {
  currentNodeId: string;
  visitedNodes: Set<string>;
  choiceHistory: ChoiceHistoryEntry[];
  currentAct: number;
  currentChapter: number;
}

// ─── Serialized Shape ─────────────────────────────────────────

export interface SerializedGameState {
  character: CharacterState;
  inventory: string[];
  equipped: EquippedSlots;
  story: {
    currentNodeId: string;
    visitedNodes: string[];
    choiceHistory: ChoiceHistoryEntry[];
    currentAct: number;
    currentChapter: number;
  };
  flags: Record<string, boolean>;
  traits: Record<string, TraitState>;
  relationships: Record<string, number>;
  quests: Record<string, string>;
  gold: number;
  abilityTiers: Record<string, number>;
  achievementCounters: Record<string, number>;
  achievementFlags: Record<string, boolean>;
  skillLevels: Record<string, number>;
  factionReputation: Record<string, number>;
  discoveredLocations: string[];
  currentLocationId: string;
  playtime: number;
}

// ─── Legacy ID Migration ─────────────────────────────────────
// Maps old frostspire/winterhold internal IDs to new gallowcrest IDs.
// Applied during deserialization to preserve backward compat with old saves.

const LEGACY_ID_MAP: Record<string, string> = {
  // Location IDs
  'frostspire': 'gallowcrest',
  'frostspire-dark-council': 'gallowcrest-dark-council',
  'frostspire-council-chambers': 'gallowcrest-council-chambers',
  'frostspire-monastery': 'gallowcrest-monastery',
  'frostspire-mountains': 'gallowcrest-mountains',
  'frostspire-road': 'gallowcrest-road',
  // Node IDs
  'journey-to-frostspire': 'journey-to-gallowcrest',
  'enter-frostspire': 'enter-gallowcrest',
  // State flag keys
  'scouted-frostspire-entry': 'scouted-gallowcrest-entry',
  'close-call-entering-frostspire': 'close-call-entering-gallowcrest',
  'frostspire-shrine-cleansed': 'gallowcrest-shrine-cleansed',
  'frostspire_evacuated': 'gallowcrest_evacuated',
  // Quest IDs
  'defend-frostspire': 'defend-gallowcrest',
};

function migrateId(id: string): string {
  return LEGACY_ID_MAP[id] ?? id;
}

function migrateIds(ids: string[]): string[] {
  return ids.map(migrateId);
}

function migrateKeys<T>(record: Record<string, T>): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(record)) {
    result[migrateId(key)] = value;
  }
  return result;
}

// ─── GameState ────────────────────────────────────────────────

export class GameState {
  character: CharacterState;
  inventory: string[];
  equipped: EquippedSlots;
  story: StoryState;
  flags: Map<string, boolean>;
  traits: Map<string, TraitState>;
  relationships: Map<string, number>;
  quests: Map<string, string>;
  gold: number;
  abilityTiers: Map<string, number>;
  achievementCounters: Map<string, number>;
  achievementFlags: Map<string, boolean>;
  skillLevels: Map<string, number>;
  factionReputation: Map<string, number>;
  discoveredLocations: Set<string>;
  currentLocationId: string;
  playtime: number;

  private events: EventSystem | null = null;

  constructor() {
    this.character = {
      name: '',
      classId: 'frostblade_fighter',
      level: 1,
      xp: 0,
      hp: 10,
      hpMax: 10,
      frostbite: 0,
      corruption: 0,
      stats: {
        strength: 10,
        dexterity: 10,
        constitution: 10,
        intelligence: 10,
        wisdom: 10,
        charisma: 10,
      },
      unspentStatPoints: 0,
    };
    this.inventory = [];
    this.equipped = {};
    this.story = {
      currentNodeId: '',
      visitedNodes: new Set(),
      choiceHistory: [],
      currentAct: 1,
      currentChapter: 1,
    };
    this.flags = new Map();
    this.traits = new Map();
    this.relationships = new Map();
    this.quests = new Map();
    this.gold = 0;
    this.abilityTiers = new Map();
    this.achievementCounters = new Map();
    this.achievementFlags = new Map();
    this.skillLevels = new Map();
    this.factionReputation = new Map();
    this.discoveredLocations = new Set();
    this.currentLocationId = '';
    this.playtime = 0;
  }

  /** Attach an EventSystem for state change notifications. */
  setEventSystem(events: EventSystem): void {
    this.events = events;
  }

  // ─── Factory ────────────────────────────────────────────────

  /** Create a new game state from a class definition and character name. */
  static createNew(classId: ClassId, name: string, startNodeId: string = 'prologue-awakening'): GameState {
    const classDef = getClass(classId);
    const state = new GameState();

    state.character = {
      name,
      classId,
      level: 1,
      xp: 0,
      hp: classDef.startingHP,
      hpMax: classDef.startingHP,
      frostbite: 0,
      corruption: 0,
      stats: { ...classDef.startingStats },
      unspentStatPoints: 0,
    };

    state.story = {
      currentNodeId: startNodeId,
      visitedNodes: new Set(),
      choiceHistory: [],
      currentAct: 1,
      currentChapter: 1,
    };

    state.gold = 0;

    return state;
  }

  // ─── Stat Access ────────────────────────────────────────────

  /** Get a base stat value. */
  getStat(statName: StatName): number {
    return this.character.stats[statName];
  }

  /** Get effective stat value including equipment bonuses. */
  getEffectiveStat(statName: StatName): number {
    let value = this.character.stats[statName];

    // Equipment bonuses would be resolved here via item lookup.
    // For now, return base stat. Equipment integration will be added
    // when the item system has stat_modifiers on equipped items.

    return value;
  }

  // ─── Stat Mutation ──────────────────────────────────────────

  /**
   * Modify a game stat by key. Handles special keys:
   * - experience_points → character.xp
   * - gold → this.gold
   * - hit_points → character.hp (clamped to 0..hpMax)
   * - frostbite → character.frostbite (clamped to 0..100)
   * - corruption / corruption_points → character.corruption (clamped to 0..100)
   * - Any StatName → character.stats[name]
   */
  modifyStat(key: string, amount: number): void {
    switch (key) {
      case 'experience_points':
        this.character.xp = Math.max(0, this.character.xp + amount);
        this.emit('xpChanged', { xp: this.character.xp, change: amount });
        break;
      case 'gold':
        this.gold = Math.max(0, this.gold + amount);
        this.emit('goldChanged', { gold: this.gold, change: amount });
        break;
      case 'hit_points':
        this.character.hp = Math.max(0, Math.min(this.character.hpMax, this.character.hp + amount));
        this.emit('hpChanged', { hp: this.character.hp, change: amount });
        break;
      case 'frostbite':
        this.character.frostbite = Math.max(0, Math.min(100, this.character.frostbite + amount));
        this.emit('frostbiteChanged', { frostbite: this.character.frostbite, change: amount });
        break;
      case 'corruption':
      case 'corruption_points':
        this.character.corruption = Math.max(0, Math.min(100, this.character.corruption + amount));
        this.emit('corruptionChanged', { corruption: this.character.corruption, change: amount });
        break;
      default: {
        // Check if it's a standard stat name
        const statNames: StatName[] = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
        if (statNames.includes(key as StatName)) {
          this.character.stats[key as StatName] += amount;
          this.emit('statChanged', { stat: key, value: this.character.stats[key as StatName], change: amount });
        }
        break;
      }
    }
  }

  // ─── Flags ──────────────────────────────────────────────────

  setFlag(key: string, value: boolean): void {
    this.flags.set(key, value);
    this.emit('flagSet', { key, value });
  }

  getFlag(key: string): boolean {
    return this.flags.get(key) ?? false;
  }

  // ─── Relationships ──────────────────────────────────────────

  modifyRelationship(npc: string, amount: number): void {
    const current = this.relationships.get(npc) ?? 0;
    const newValue = Math.max(-100, Math.min(100, current + amount));
    this.relationships.set(npc, newValue);
    this.emit('relationshipChanged', { npc, value: newValue, change: amount });
  }

  getRelationship(npc: string): number {
    return this.relationships.get(npc) ?? 0;
  }

  // ─── Traits ─────────────────────────────────────────────────

  /**
   * Modify a trait level by +1 (increase) or -1 (decrease).
   * If traitData is provided and the trait doesn't exist, creates it.
   */
  modifyTrait(key: string, amount: number, traitData?: { name: string; description: string; category?: string }): void {
    const existing = this.traits.get(key);
    if (existing) {
      existing.level = Math.max(0, existing.level + amount);
      if (existing.level === 0) {
        this.traits.delete(key);
        this.emit('traitRemoved', { key });
      } else {
        this.emit('traitChanged', { key, trait: existing, change: amount });
      }
    } else if (amount > 0) {
      const newTrait: TraitState = {
        name: traitData?.name ?? key,
        level: amount,
        description: traitData?.description ?? '',
        category: traitData?.category ?? 'personality',
      };
      this.traits.set(key, newTrait);
      this.emit('traitAcquired', { key, trait: newTrait });
    }
  }

  getTrait(key: string): TraitState | undefined {
    return this.traits.get(key);
  }

  // ─── Inventory ──────────────────────────────────────────────

  addItem(id: string): void {
    this.inventory.push(id);
    this.emit('itemAdded', { id });
  }

  removeItem(id: string): boolean {
    const index = this.inventory.indexOf(id);
    if (index >= 0) {
      this.inventory.splice(index, 1);
      this.emit('itemRemoved', { id });
      return true;
    }
    return false;
  }

  hasItem(id: string): boolean {
    return this.inventory.includes(id);
  }

  // ─── Quests ─────────────────────────────────────────────────

  setQuest(id: string, status: string): void {
    this.quests.set(id, status);
    this.emit('questUpdated', { id, status });
  }

  getQuest(id: string): string | undefined {
    return this.quests.get(id);
  }

  // ─── Serialization ─────────────────────────────────────────

  /** Serialize state to a plain JSON-safe object. */
  serialize(): SerializedGameState {
    return {
      character: { ...this.character },
      inventory: [...this.inventory],
      equipped: { ...this.equipped },
      story: {
        currentNodeId: this.story.currentNodeId,
        visitedNodes: Array.from(this.story.visitedNodes),
        choiceHistory: [...this.story.choiceHistory],
        currentAct: this.story.currentAct,
        currentChapter: this.story.currentChapter,
      },
      flags: Object.fromEntries(this.flags),
      traits: Object.fromEntries(this.traits),
      relationships: Object.fromEntries(this.relationships),
      quests: Object.fromEntries(this.quests),
      gold: this.gold,
      abilityTiers: Object.fromEntries(this.abilityTiers),
      achievementCounters: Object.fromEntries(this.achievementCounters),
      achievementFlags: Object.fromEntries(this.achievementFlags),
      skillLevels: Object.fromEntries(this.skillLevels),
      factionReputation: Object.fromEntries(this.factionReputation),
      discoveredLocations: Array.from(this.discoveredLocations),
      currentLocationId: this.currentLocationId,
      playtime: this.playtime,
    };
  }

  /** Deserialize a plain JSON object back into a GameState instance. */
  static deserialize(json: SerializedGameState): GameState {
    const state = new GameState();

    state.character = {
      ...json.character,
      stats: { ...json.character.stats },
    };
    state.inventory = [...json.inventory];
    state.equipped = { ...json.equipped };
    state.story = {
      currentNodeId: migrateId(json.story.currentNodeId),
      visitedNodes: new Set(migrateIds(json.story.visitedNodes)),
      choiceHistory: json.story.choiceHistory.map(entry => ({
        ...entry,
        nodeId: migrateId(entry.nodeId),
        choiceId: migrateId(entry.choiceId),
      })),
      currentAct: json.story.currentAct,
      currentChapter: json.story.currentChapter,
    };
    state.flags = new Map(Object.entries(migrateKeys(json.flags)));
    state.traits = new Map(Object.entries(json.traits));
    state.relationships = new Map(Object.entries(json.relationships));
    state.quests = new Map(Object.entries(migrateKeys(json.quests)));
    state.gold = json.gold;
    state.abilityTiers = new Map(Object.entries(json.abilityTiers));
    state.achievementCounters = new Map(Object.entries(json.achievementCounters));
    state.achievementFlags = new Map(Object.entries(json.achievementFlags));
    state.skillLevels = new Map(Object.entries(json.skillLevels ?? {}));
    state.factionReputation = new Map(Object.entries(json.factionReputation ?? {}));
    state.discoveredLocations = new Set(migrateIds(json.discoveredLocations ?? []));
    state.currentLocationId = migrateId(json.currentLocationId ?? '');
    state.playtime = json.playtime;

    return state;
  }

  // ─── Internal ───────────────────────────────────────────────

  private emit(event: string, data?: unknown): void {
    this.events?.emit(event, data);
  }
}
